import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import get from 'lodash-es/get'

import { EMPTY_STRING } from '../../../../constants/common'
import { stopPropagation } from '../../../../utils/events'
import {
  formatFilesToBase64,
  getMegabytesFromBytes,
} from '../../../../utils/file'
import { ConfirmationModal } from '../../../Confirmation'
import { getFilesCheckedList } from '../../File.utils'
import { FileErrorTooLargeModal } from '../../FileErrorTooLargeModal'

import {
  FILE_UPLOAD_DROPZONE_OPTIONS,
  REACT_DROPZONE_ERROR_CODES,
} from './useFileDropzone.constants'
import {
  isReactDropzoneErrorTooLarge,
  isReactDropzoneErrorTooMany,
  toFailedFileListItems,
  toFileListItem,
  validateFiles,
} from './useFileDropzone.utils'

/**
 * WARNING!
 *
 * While using hook be sure that file input is existing in DOM, cause it's require for `Firefox`.
 * Without `ref` from `getFileAttachInputProps` in file input `Firefox` won't open file select window.
 *
 */
export function useFileDropzone({
  getFiles = getFilesCheckedList,
  options = FILE_UPLOAD_DROPZONE_OPTIONS,
  outboundUuid,
  uploadedFilesLength = 0,
  uploadParams,
  useBase64,
  onCloseModal,
  onDrop,
  onDropRejected,
  onFileDialogCancel,
  onFinishUpload,
  onPreload,
  onSetModal,
  onStartUpload,
  onUploadErrors,
}) {
  const { t } = useTranslation()

  async function handleUpload({ errors, files, params = uploadParams }) {
    // A rejected batch must still reach the callbacks, or the placeholders never clear
    const checkedFiles = await getFiles({
      files,
      outboundUuid,
      uploadParams: params,
    }).catch(rejection => toFailedFileListItems(files, rejection))

    const { acceptedFiles, rejectedFiles } = validateFiles(checkedFiles)

    const errorFiles = errors.concat(rejectedFiles)

    onFinishUpload(acceptedFiles, errorFiles)

    if (errorFiles.length > 0) {
      onUploadErrors?.(errorFiles)
    }
  }

  async function startUpload({ accepted, errors, filesList }) {
    onStartUpload?.(filesList)

    if (useBase64) {
      const f = await formatFilesToBase64(accepted)

      onFinishUpload(f)

      return
    }

    if (onPreload) {
      onPreload({
        errors,
        filesList,
        onUpload: handleUpload,
      })
    } else {
      handleUpload({ errors, files: filesList })
    }
  }

  function handleConfirmTooLargeModal(args) {
    return event => {
      stopPropagation(event)
      onCloseModal()

      startUpload(args)
    }
  }

  async function handleDrop(accepted, rejected) {
    const filesList = await Promise.all(accepted.map(toFileListItem))

    onDrop?.()

    const { errors, hasTooManyFiles, tooLargeFiles } = rejected.reduce(
      (accumulator, current) => {
        const error = get(current, ['errors', '0', 'code'])
        const name = get(current, ['file', 'name'], EMPTY_STRING)
        const mime_type = get(current, ['file', 'type'])

        // react-dropzone 19+ partially accepts an over-limit drop, so a too-many-files rejection must block the whole batch
        if (isReactDropzoneErrorTooMany(error)) {
          accumulator.hasTooManyFiles = true

          return accumulator
        }

        if (isReactDropzoneErrorTooLarge(error)) {
          accumulator.tooLargeFiles.push(name)

          return accumulator
        }

        if (error) {
          accumulator.errors.push({
            error: {
              isPersistent: true,
              text: REACT_DROPZONE_ERROR_CODES[error],
            },
            mime_type,
            name,
            uploading: true,
          })
        }

        return accumulator
      },
      { errors: [], hasTooManyFiles: false, tooLargeFiles: [] }
    )

    const remainingFileSize = options.maxFiles - uploadedFilesLength

    if (
      options.maxFiles &&
      (hasTooManyFiles ||
        remainingFileSize < filesList.length ||
        remainingFileSize < errors.length)
    ) {
      onSetModal(
        <ConfirmationModal
          actionButtonsProps={{
            Cancel: null,
            submitText: t('Modal.CommonConfirmBtnKey'),
          }}
          description={t('Common.FileMaxNumberErrorKey', {
            count: options.maxFiles,
          })}
          onClose={onCloseModal}
          onSubmit={onCloseModal}
        />
      )

      return
    }

    const argsForStartUpload = {
      accepted,
      errors,
      filesList,
    }

    if (tooLargeFiles.length > 0) {
      onSetModal(
        <FileErrorTooLargeModal
          files={tooLargeFiles}
          sizeLimit={getMegabytesFromBytes(options.maxSize)}
          onConfirm={handleConfirmTooLargeModal(argsForStartUpload)}
        />
      )
    } else {
      startUpload(argsForStartUpload)
    }
  }

  const dropzoneOptions = {
    ...options,
    onDrop: handleDrop,
    onDropRejected: onDropRejected,
    onFileDialogCancel: onFileDialogCancel,
  }

  const dropzoneProps = useDropzone(dropzoneOptions)

  return dropzoneProps
}
